// src/utils/websocket.js
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

class WebSocketService {
  constructor() {
    this.stompClient = null
    this.connected = false
    this.connecting = false
    this.subscriptions = new Map()
    this.token = null
    this.retryCount = 0
    this.maxRetries = 10
    this.retryTimer = null
  }

  /**
   * 连接 WebSocket
   * ★ 修正：统一使用相对路径 /ws（不包含 /api 前缀）
   *   开发环境通过 devServer 代理转发，生产环境由 Nginx 转发
   */
  connect(token) {
    if (this.connected || this.connecting) return

    // 检查 token 是否有效
    if (!this._isTokenValid(token)) {
      console.warn('[WS] token无效或已过期，跳过连接')
      this._scheduleRetry()
      return
    }

    this.connecting = true
    this.token = token

    // ★ 关键修复：统一使用相对路径 /ws（不包含 /api 前缀）
    const wsUrl = `/ws?token=${token}`

    this.stompClient = new Client({
      // 禁用 SockJS iframe 传输，避免 X-Frame-Options 冲突和 iframe.html 404
      webSocketFactory: () => new SockJS(wsUrl, null, {
        transports: ['websocket', 'xhr-streaming', 'xhr-polling']
      }),
      connectHeaders: { Authorization: `Bearer ${token}` },
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      reconnectDelay: 10000, // 断线重连间隔

      onConnect: () => {
        this.connected = true
        this.connecting = false
        this.retryCount = 0
        console.log('[WS] connected')
        this._resubscribeAll()
      },

      onDisconnect: () => {
        this.connected = false
        this.connecting = false
        console.log('[WS] disconnected')
      },

      onStompError: (frame) => {
        const message = frame.headers?.message || ''
        console.error('[WS] STOMP error:', message)
        this.connected = false
        this.connecting = false

        // 认证失败则停止自动重试，由上层决定何时重连
        if (
          message.includes('401') ||
          message.includes('Unauthorized') ||
          message.includes('token')
        ) {
          console.warn('[WS] 认证失败，停止重试，等待 token 刷新后重新连接')
          this._deactivate()
          this._scheduleRetry()
        }
      },
    })

    this.stompClient.activate()
  }

  /**
   * 检查 token 是否有效（未过期且格式正确）
   */
  _isTokenValid(token) {
    if (!token || typeof token !== 'string') return false
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return false
      const payload = JSON.parse(atob(parts[1]))
      if (!payload.exp) return true // 无过期时间，视为有效
      // 要求 token 至少还有 30 秒有效期
      return payload.exp * 1000 > Date.now() + 30000
    } catch {
      return false
    }
  }

  /**
   * 指数退避重试
   */
  _scheduleRetry() {
    if (this.retryCount >= this.maxRetries) {
      console.warn(`[WS] 已达最大重试次数(${this.maxRetries})，等待下次手动触发`)
      return
    }

    if (this.retryTimer) clearTimeout(this.retryTimer)

    // 指数退避：10s, 20s, 40s, 60s, 60s...
    const delay = Math.min(10000 * Math.pow(2, this.retryCount), 60000)
    this.retryCount++

    console.log(`[WS] ${delay / 1000}秒后重试连接 (第${this.retryCount}次)`)
    this.retryTimer = setTimeout(() => {
      this.connecting = false
      const token = localStorage.getItem('token')
      if (token) this.connect(token)
    }, delay)
  }

  /**
   * 停用当前连接
   */
  _deactivate() {
    try {
      if (this.stompClient) {
        this.stompClient.deactivate()
        this.stompClient = null
      }
    } catch (e) {
      // ignore
    }
    this.connected = false
    this.connecting = false
  }

  /**
   * 订阅目的地
   * @param {string} destination - 如 /user/queue/notifications
   * @param {Function} callback - 收到消息时的回调
   * @returns {string} 订阅key，用于取消订阅
   */
  subscribe(destination, callback) {
    const subKey = `${destination}::${Date.now()}`

    if (this.connected && this.stompClient) {
      const sub = this.stompClient.subscribe(destination, (message) => {
        try {
          const payload = JSON.parse(message.body)
          callback(payload)
        } catch {
          callback(message.body)
        }
      })
      this.subscriptions.set(subKey, { destination, callback, sub })
    } else {
      // 未连接时暂存，待连接成功后自动订阅
      this.subscriptions.set(subKey, { destination, callback, pending: true })
    }

    return subKey
  }

  /**
   * 取消订阅
   */
  unsubscribe(subKey) {
    const entry = this.subscriptions.get(subKey)
    if (entry?.sub) {
      entry.sub.unsubscribe()
    }
    this.subscriptions.delete(subKey)
  }

  /**
   * 主动断开连接
   */
  disconnect() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this._deactivate()
    this.subscriptions.clear()
    this.retryCount = 0
  }

  /**
   * 使用新 token 重连（通常在 token 刷新后调用）
   */
  reconnectWithToken(newToken) {
    this.disconnect()
    this.retryCount = 0
    // 短暂延迟确保旧连接完全关闭
    setTimeout(() => this.connect(newToken), 500)
  }

  /**
   * 重新订阅所有暂存的订阅
   */
  _resubscribeAll() {
    // 延迟确保连接完全稳定
    setTimeout(() => {
      for (const [subKey, entry] of this.subscriptions) {
        if (entry.pending && this.stompClient?.connected) {
          try {
            const sub = this.stompClient.subscribe(entry.destination, (message) => {
              try {
                const payload = JSON.parse(message.body)
                entry.callback(payload)
              } catch {
                entry.callback(message.body)
              }
            })
            entry.sub = sub
            entry.pending = false
          } catch (e) {
            console.warn('[WS] Resubscribe failed:', e.message)
          }
        }
      }
    }, 500)
  }
}

export const wsService = new WebSocketService()